import React, { useState, useEffect, useCallback } from 'react';
import { kpiApi } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

/**
 * The Accident register's four severities, each with its own ladder.
 *
 * This KPI never fitted the ordinary marking ladder. The register records four
 * different kinds of event that do NOT weigh the same, and they combine into
 * ONE mark — so there was nothing sensible to put on a single threshold table.
 * What went there instead was an identity ladder (4->4, 3->3, 2->2, 1->1) that
 * passed the finished mark straight through, while the weights that actually
 * decided it sat hardcoded in Python. The one control HR could reach did
 * nothing, and the four that mattered could not be reached at all.
 *
 * Each severity is read on its COUNT and contributes an ADJUSTMENT to a base
 * score. Not four marks combined by some rule: a near miss is a BONUS — being
 * told about one is the behaviour this KPI exists to encourage — while the
 * other three are penalties. Any "worst of" or "average of" rule would mark a
 * plant down for reporting near misses, which is backwards.
 *
 * Every number here is the server's. The ladders are stored once and govern
 * every KPI scored from the register, so there is no second copy to drift.
 */

const LABEL = {
  near_miss: 'Near Miss', minor: 'Minor', major: 'Major', fatal: 'Fatal',
};
// The register's own colours, so a row reads the same here as on the chips.
const TINT = {
  near_miss: 'bg-[#E7F6EE] border-[#BFE3CF] text-[#166534]',
  minor: 'bg-[#FDF6E3] border-[#EFDFAE] text-[#854D0E]',
  major: 'bg-[#FDECEC] border-[#F3C9C9] text-[#B91C1C]',
  fatal: 'bg-[#11182B] border-[#11182B] text-white',
};
const HELP = {
  near_miss: 'Reporting one is good. This is the only severity that can ADD to the mark.',
  minor: 'Deducted for each count band reached.',
  major: 'Deducted for each count band reached.',
  fatal: 'Settles the mark outright — not a deduction that a tidy near-miss log can offset.',
};

const countOf = (r) => (r.gte ?? r.lte ?? '');
const isElse = (r) => 'else' in r;

/** One severity's steps. */
function Severity({ sev, rungs, onChange, disabled }) {
  const set = (i, patch) =>
    onChange(rungs.map((r, n) => (n === i ? { ...r, ...patch } : r)));
  const addStep = () => {
    // Inserted ABOVE the catch-all, which must stay last or it swallows every
    // step below it — the walk stops at the first rung that matches.
    const i = rungs.findIndex(isElse);
    const step = { gte: (Number(countOf(rungs[0])) || 0) + 1, adjust: 0 };
    onChange(i < 0 ? [...rungs, step]
      : [...rungs.slice(0, i), step, ...rungs.slice(i)]);
  };
  return (
    <div className="mb-3" data-testid={`acc-sev-${sev}`}>
      <div className="flex items-center gap-2 mb-1">
        <span className={`text-[11px] font-semibold rounded-full px-2.5 py-0.5 border ${TINT[sev]}`}>
          {LABEL[sev]}
        </span>
        <span className="text-[10.5px] text-[#98A1B2]">{HELP[sev]}</span>
      </div>
      <div className="space-y-1">
        {rungs.map((r, i) => (
          <div key={i} className="flex items-center gap-1.5 text-[11.5px]">
            {isElse(r) ? (
              <span className="text-[#5A6377] w-[128px]">anything else</span>
            ) : (
              <>
                <span className="text-[#98A1B2]">at least</span>
                <Input type="number" min="0" value={countOf(r)} disabled={disabled}
                  data-testid={`acc-${sev}-count-${i}`}
                  onChange={(e) => set(i, { gte: Number(e.target.value), lte: undefined })}
                  className="h-7 w-[58px] text-[11.5px] px-1.5" />
                <span className="text-[#98A1B2] w-[44px]">
                  {Number(countOf(r)) === 1 ? 'report' : 'reports'}
                </span>
              </>
            )}
            <span className="text-[#B4B9CC]">→</span>
            {r.force !== undefined && r.force !== null ? (
              <span className="text-[#B91C1C] font-medium">
                mark is {r.force} — whatever else happened
              </span>
            ) : (
              <>
                <Input type="number" step="0.5" value={r.adjust ?? 0} disabled={disabled}
                  data-testid={`acc-${sev}-adjust-${i}`}
                  onChange={(e) => set(i, { adjust: Number(e.target.value) })}
                  className="h-7 w-[64px] text-[11.5px] px-1.5" />
                <span className="text-[#98A1B2]">
                  {(r.adjust ?? 0) > 0 ? 'added' : (r.adjust ?? 0) < 0 ? 'taken off' : 'no change'}
                </span>
              </>
            )}
            {!isElse(r) && rungs.length > 2 && !disabled && (
              <button type="button" title="Remove this step"
                data-testid={`acc-${sev}-remove-${i}`}
                onClick={() => onChange(rungs.filter((_, n) => n !== i))}
                className="ml-1 text-[#C2151F] opacity-60 hover:opacity-100">✕</button>
            )}
          </div>
        ))}
      </div>
      {!disabled && (
        <button type="button" onClick={addStep} data-testid={`acc-${sev}-add`}
          className="mt-1 text-[10.5px] text-[#2563EB] hover:underline">+ add a step</button>
      )}
    </div>
  );
}

export default function AccidentLadders({ activeUnit = '', plants = [], canEdit = true }) {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  // Which plants this edit is for. Mirrors the marking ladder's scope picker so
  // the two controls do not teach different habits.
  const [scopeOne, setScopeOne] = useState('');

  const load = useCallback(async (unit) => {
    try {
      const r = await kpiApi.accidentLadders(unit || undefined);
      setCfg(r.data); setDirty(false);
    } catch { toast.error('Could not load the accident ladders'); }
  }, []);

  useEffect(() => { load(scopeOne); }, [load, scopeOne]);

  if (!cfg) return <div className="text-[11px] text-[#98A1B2]">Loading…</div>;

  const severities = cfg.severities || [];
  const setLadder = (sev, rungs) => {
    setCfg((c) => ({ ...c, ladders: { ...c.ladders, [sev]: rungs } }));
    setDirty(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      const r = await kpiApi.saveAccidentLadders({
        scope: scopeOne ? [scopeOne] : [], base: cfg.base, ladders: cfg.ladders });
      toast.success(r.data.note);
      await load(scopeOne);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save');
    } finally { setBusy(false); }
  };

  const removeOverride = async () => {
    if (!window.confirm(
      `Put ${scopeOne} back on the shared ladders?\n\n`
      + 'Its own numbers are discarded and it is marked like every other plant '
      + 'from the next open month.')) return;
    setBusy(true);
    try {
      const r = await kpiApi.saveAccidentLadders({ scope: [scopeOne], mode: 'remove' });
      toast.success(r.data.note); await load(scopeOne);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not remove'); }
    finally { setBusy(false); }
  };

  const overrides = cfg.plants_with_an_override || [];
  const onOwn = scopeOne && overrides.includes(scopeOne);

  return (
    <div data-testid="accident-ladders">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
        <div>
          <div className="text-[12px] font-semibold text-[#11182B]">
            Accident register — one ladder per severity
          </div>
          {/* Said plainly, because this is the question the screen exists to
              answer: four counts, one mark. */}
          <div className="text-[10.5px] text-[#98A1B2] mt-0.5 max-w-[460px]">
            Each severity is read on how many were recorded that month and changes the
            mark by the amount you set. One mark comes out, so this stays one KPI on
            one template.
          </div>
        </div>
        {plants.length > 0 && (
          <div className="flex flex-col items-end gap-1">
            <span className="text-[10px] uppercase tracking-wide text-[#B4B9CC]">Applies to</span>
            <div className="flex items-center gap-1.5 flex-wrap justify-end">
              {[{ name: '', short: 'All Plants' }, ...plants].map((p) => (
                <button key={p.name || 'all'} type="button" data-testid={`acc-scope-${p.name || 'all'}`}
                  onClick={() => setScopeOne(p.name)}
                  title={!p.name ? 'The shared ladders. Every plant uses them unless it has its own.'
                    : overrides.includes(p.name) ? `${p.name} has its own ladders.`
                      : `${p.name} currently uses the shared ladders.`}
                  className={`text-[11px] rounded-full px-2.5 py-0.5 border transition-colors ${
                    scopeOne === p.name
                      ? 'bg-[#E8EFFD] border-[#2563EB] text-[#172554] font-medium'
                      : 'bg-white border-[#E6E9F0] text-[#5A6377] hover:bg-[#F5F8FF]'}`}>
                  {p.short || p.name || 'All Plants'}
                  {p.name && overrides.includes(p.name) && (
                    <span className="ml-1 text-[#2563EB]">•</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Which ladders are on screen RIGHT NOW. A plant quietly showing the
          shared set, edited and saved, is how an override gets created by
          accident — or missed entirely. */}
      <div className="text-[10.5px] mb-2">
        {cfg.scope === 'unit' ? (
          <span className="text-[#2563EB] font-medium">● {scopeOne} has its own ladders</span>
        ) : scopeOne ? (
          <span className="text-[#98A1B2]">
            {scopeOne} uses the shared ladders. Saving here gives it its own.
          </span>
        ) : (
          <span className="text-[#98A1B2]">
            The shared ladders — used by every plant that has none of its own
            {overrides.length > 0 && ` (${overrides.join(', ')} ${overrides.length === 1 ? 'has' : 'have'})`}.
          </span>
        )}
      </div>

      <div className="flex items-center gap-2 mb-3 text-[11.5px]">
        <span className="text-[#5A6377]">A clean month starts at</span>
        <Input type="number" step="0.5" min="0" max="4" value={cfg.base} disabled={!canEdit}
          data-testid="acc-base"
          onChange={(e) => { setCfg((c) => ({ ...c, base: Number(e.target.value) })); setDirty(true); }}
          className="h-7 w-[58px] text-[11.5px] px-1.5" />
        <span className="text-[#98A1B2]">marks</span>
      </div>

      {severities.map((sev) => (
        <Severity key={sev} sev={sev} rungs={cfg.ladders[sev] || []}
          disabled={!canEdit} onChange={(r) => setLadder(sev, r)} />
      ))}

      <div className="text-[10.5px] text-[#98A1B2] border-t border-[#EEF2FA] pt-2 mt-1">
        The mark is the base plus every adjustment, kept inside 0–{4}.
        {(cfg.applies_to_kpis || []).length > 1 && (
          <> Governs {cfg.applies_to_kpis.length} KPIs: {cfg.applies_to_kpis.join(', ')}.</>
        )}
      </div>

      {canEdit && (
        <div className="flex items-center gap-2 mt-3">
          <Button size="sm" onClick={save} disabled={busy || !dirty} data-testid="acc-save">
            {busy ? 'Saving…' : scopeOne ? `Save for ${scopeOne}` : 'Save shared ladders'}
          </Button>
          {onOwn && (
            <button type="button" onClick={removeOverride} disabled={busy}
              data-testid="acc-remove-override"
              className="text-[11px] text-[#C2151F] hover:underline">
              Put {scopeOne} back on the shared ladders
            </button>
          )}
          {dirty && <span className="text-[10.5px] text-[#B14A0E]">unsaved changes</span>}
        </div>
      )}
    </div>
  );
}
